import { useCallback, useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs'
import { Absence, AbsencesChargees } from '../../api/Absence/types';
import { AbsenceApi } from '../../api/Absence';
import { DemandeAbsenceApi } from '../../api/DemandeAbsence';
import { FerieApi } from '../../api/Ferie/FerieApi';
import { VacancesApi } from '../../api/VacancesScolaires/VacancesApi';
import { useSessionStore } from '../../lib/Store/sessionStore.component';

export const useCalendrierUniverselState = (initialMode: 'global' | 'liste' | 'jour' | 'month' = 'global', value?: string) => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();
  const [modeCalendrier, setModeCalendrier] = useState<'global' | 'liste' | 'jour' | 'month'>(initialMode);
  const [donnees, setDonnees] = useState<AbsencesChargees>({annees: [], absences: [], demandes: [], feries: [], vacances: []})
  const [currentDate, setCurrentDate] = useState(value || dayjs().format('YYYY-MM-DD'))
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null)

  const handleVisualiseAbsence = useCallback((absence: Absence) => {
    setSelectedAbsence(absence)
  }, [])

  const closeAbsenceDetail = () => {
    setSelectedAbsence(null)
  }

  // Optimisation: créer un Set des dates fériées pour recherche O(1)
  const feriesSet = useMemo(() => {
    return new Set(donnees.feries.map(day => day.format('YYYY-MM-DD')));
  }, [donnees.feries]);

  const isFerie = useCallback((date: Date) => {
    return feriesSet.has(dayjs(date).format('YYYY-MM-DD'));
  }, [feriesSet])

  const toggleVacances = useCallback((label: string) => {
    setDonnees((oldDonnees) => ({
      ...oldDonnees,
      vacances: oldDonnees.vacances.map((vacance) => vacance.label === label ? {...vacance, selected: !vacance.selected} : vacance)
    }))
  }, [])

  // Suivi synchrone (via ref) des années déjà chargées/en cours de chargement,
  // pour éviter les appels API dupliqués lors de clics rapprochés sur le calendrier
  // (donnees.annees n'est mis à jour qu'au prochain rendu, ce qui créait une
  // fenêtre de course où plusieurs appels pouvaient passer le garde-fou).
  const anneesChargeesRef = useRef<Set<number>>(new Set())

  const chargementDonnees = useCallback((date: Date) => {
    const annee = date.getFullYear()
    if(anneesChargeesRef.current.has(annee)) return
    anneesChargeesRef.current.add(annee)

    AbsenceApi.loadAbsence(currentLogin?.identifiant || 0, dayjs(date).startOf('year').format('YYYY-MM-DD'), dayjs(date).endOf('year').format('YYYY-MM-DD'))
      .then((response) => {
        const absences = Array.isArray(response) ? response : []
        setDonnees((oldDonnees) =>({
          ...oldDonnees,
          absences: [...oldDonnees.absences, ...absences]
        }))
      })

    DemandeAbsenceApi.loadDemandesAbsence(`duree=1&withNatureAbsence=true&withDoc=true&statut=0&valide=0&identifiantAgent=${currentLogin?.identifiant}&dateRefDebut=${dayjs(date).startOf('year').format('YYYY-MM-DD')}&dateRefFin=${dayjs(date).endOf('year').format('YYYY-MM-DD')}&perPage=300`)
      .then((response) => {
        const demandes = Array.isArray(response) ? response : []
        setDonnees((oldDonnees) =>({
          ...oldDonnees,
          demandes: [...oldDonnees.demandes, ...demandes]
        }))
      })

    FerieApi.listeFeries(dayjs(date).startOf('year'), dayjs(date).endOf('year'))
      .then((response) => {
        const joursFeries: Dayjs[] = (Array.isArray(response) ? response : []).map((jour: string) => dayjs(jour));
        setDonnees((oldDonnees) => ({
          ...oldDonnees,
          feries: [...oldDonnees.feries, ...joursFeries]
        }))
      })

    VacancesApi.listeVacances(currentLogin?.codePostal || '', date.getFullYear())
      .then((response) => {
        const vacancesByZone = response && typeof response === 'object' ? response : {}
        if(Object.keys(vacancesByZone).length > 0){
          setDonnees((oldDonnees) => ({
            ...oldDonnees,
            vacances: Object.keys(vacancesByZone).sort((a, b) => a < b ? -1 : 1).map((zone, index) => ({
              label: zone,
              jours: [
                ...(oldDonnees.vacances.find((vacance) => vacance.label === zone)?.jours || []),
                ...((Array.isArray(vacancesByZone[zone]) ? vacancesByZone[zone] : []).filter((dateStr: string) => !(oldDonnees.vacances.find((vacance) => vacance.label === zone)?.jours || [])
                                                                                  .some((oldDate) => oldDate.isSame(dateStr, 'day')))
                                  .map((dateStr: string) => dayjs(dateStr))
                )

                ],
              couleur: VacancesApi.couleursZones[index],
              selected: oldDonnees.vacances.find((vacance) => vacance.label === zone)?.selected || false,
            }))
          }))
        }
      })

    setDonnees((oldDonnees) => (oldDonnees.annees.includes(annee) ? oldDonnees : {
      ...oldDonnees,
      annees: [...oldDonnees.annees, annee]
    }))
  }, [currentLogin])

  return {
    modeCalendrier,
    donnees,
    currentDate,
    selectedAbsence,
    setModeCalendrier,
    setDonnees,
    isFerie,
    toggleVacances,
    chargementDonnees,
    setCurrentDate,
    handleVisualiseAbsence,
    closeAbsenceDetail
  };
};
